import { DatePipe } from '@angular/common';
import {ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { InspectableSession } from '../../../core/interfaces/inspection';
import { MaterialPreviewComponent, PreviewItem } from '../../../components/ui/views/material-preview/material-preview.component';
import { InspectionReportDialogComponent } from '../../../components/ui/inspection-report/inspection-report-dialog.component';
import { InspectionRulesService } from '../../../core/services/inspection-rules.service';
import {
  AcademicSchedulingOptions,
  SchedulingSubject,
} from '../../../core/interfaces/academic-appointment';
import { InspectionService } from '../../../core/services/inspection.service';
import { InspectionScope, isEmptyScope } from '../../../core/interfaces/inspection-scope';
import { InspectionScopeSummaryComponent } from '../../../components/ui/inspection-scope/inspection-scope-summary.component';
import { SessionJoinService } from '../../../core/services/academic-appointments/session-join.service';
import {
  SubjectChoiceService,
  SubjectPathGroup,
} from '../../../core/services/utils/subject-choice.service';

/** Lignes par page — même cadence que les autres listes de l'administration. */
const PAGE_SIZE = 25;

/**
 * Séances vues par l'inspection.
 *
 * <p>La liste de travail de l'inspecteur, c'est <b>ce qui reste à voir</b> : la
 * facette d'état s'ouvre donc sur « non inspectées » et non sur « toutes ». Un
 * écran qui s'ouvrirait sur l'ensemble obligerait à filtrer avant de commencer,
 * à chaque visite.</p>
 *
 * <p>Deux voies d'inspection, et l'écran dit laquelle est ouverte : rejoindre la
 * réunion pendant qu'elle se tient — l'entrée est alors journalisée, c'est elle
 * qui atteste l'inspection en temps réel — ou consulter l'enregistrement après
 * coup. Une séance sans lien ni enregistrement ne s'inspecte pas encore, et le
 * dire évite de chercher un bouton qui n'existe pas.</p>
 */
@Component({
  selector: 'app-inspector-sessions',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    TranslateModule,
    EmptyStateComponent,
    ErrorStateComponent,
    InspectionScopeSummaryComponent,
    MaterialPreviewComponent,
    InspectionReportDialogComponent,
  ],
  templateUrl: './inspector-sessions.component.html',
  styleUrl: './inspector-sessions.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectorSessionsComponent implements OnInit {
  private readonly service = inject(InspectionService);
  private readonly joinService = inject(SessionJoinService);
  private readonly subjectChoice = inject(SubjectChoiceService);
  readonly rules = inject(InspectionRulesService);

  readonly sessions = signal<InspectableSession[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);

  readonly total = signal(0);
  readonly page = signal(1);

  readonly options = signal<AcademicSchedulingOptions>({
    subjects: [], groups: [], students: [], professors: [],
  });

  /**
   * Le périmètre de l'inspecteur, tel que le serveur l'applique. Nul tant
   * qu'il n'est pas lu : un périmètre inconnu n'est pas un périmètre vide, et
   * la liste ne se cache pas derrière un résumé qu'on n'a pas encore. Vide
   * quand le superviseur n'a rien attribué : la liste l'est aussi, et c'est
   * le résumé qui le dit — pas un « aucune séance » trompeur.
   */
  readonly scope = signal<InspectionScope | null>(null);
  readonly scopeIsEmpty = computed(() => this.scope() !== null && isEmptyScope(this.scope()));

  // ── Facettes ───────────────────────────────────────────────
  /** `false` d'emblée : la liste de travail est ce qui reste à inspecter. */
  inspectedFilter: 'all' | 'yes' | 'no' = 'no';
  filterProfessorId: number | null = null;
  filterSubjectId: number | null = null;
  /**
   * La période ouvre sur le mois en cours : c'est l'horizon de travail d'un
   * inspecteur. « Toutes » lève la borne ; « dates précises » la fait choisir.
   */
  period: 'month' | 'all' | 'custom' = 'month';
  filterFrom = '';
  filterTo = '';

  /**
   * Des filtres sont posés. Un accesseur, pas un `computed` : les champs liés
   * par ngModel ne sont pas des signaux, un `computed` n'aurait aucun
   * producteur et resterait figé à sa première valeur.
   */
  get hasFilters(): boolean {
    return this.inspectedFilter !== 'no'
       || this.filterProfessorId !== null
       || this.filterSubjectId !== null
       || this.period !== 'month';
  }

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));
  readonly canPrevious = computed(() => !this.loading() && this.page() > 1);
  readonly canNext = computed(() => !this.loading() && this.page() < this.totalPages());

  /** Matières du filtre, groupées par parcours — mêmes homonymes qu'ailleurs. */
  readonly subjectGroups = computed<SubjectPathGroup[]>(() =>
    this.subjectChoice.groupByPath(this.options().subjects),
  );

  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.options().subjects);
  }

  // ── Rapport ────────────────────────────────────────────────
  /** La séance dont on rédige le rapport : le dialogue partagé fait le reste. */
  readonly target = signal<InspectableSession | null>(null);
  /** Échec d'entrée en réunion — le seul message que l'écran porte lui-même. */
  readonly joinError = signal<string | null>(null);

  openReport(s: InspectableSession): void {
    this.target.set(s);
  }

  /**
   * Le rapport est déposé : la séance quitte la liste quand celle-ci ne montre
   * que le reste à faire — c'est le résultat attendu, pas une disparition.
   */
  onReportSaved(): void {
    this.target.set(null);
    this.load();
  }

  join(s: InspectableSession): void {
    this.joinError.set(null);
    this.joinService.openMeeting(s.id, (err) => this.joinError.set(messageFrom(err)));
  }

  ngOnInit(): void {
    this.loadReferential();
    this.load();
  }

  // ── Chargement ─────────────────────────────────────────────

  applyFilters(): void {
    this.page.set(1);
    this.load();
  }

  clearFilters(): void {
    this.inspectedFilter = 'no';
    this.filterProfessorId = null;
    this.filterSubjectId = null;
    this.period = 'month';
    this.filterFrom = '';
    this.filterTo = '';
    this.applyFilters();
  }

  /** Les bornes réellement envoyées, d'après la période choisie. */
  private bounds(): { from: string | null; to: string | null } {
    if (this.period === 'all') return { from: null, to: null };
    if (this.period === 'custom') return { from: this.filterFrom || null, to: this.filterTo || null };
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: iso(first), to: iso(last) };
  }

  /** Relire tout : la liste, et le périmètre qui l'explique. */
  reload(): void {
    this.loadReferential();
    this.load();
  }

  previous(): void {
    if (!this.canPrevious()) return;
    this.page.update((p) => p - 1);
    this.load();
  }

  next(): void {
    if (!this.canNext()) return;
    this.page.update((p) => p + 1);
    this.load();
  }

  private loadReferential(): void {
    this.service.options().subscribe({
      next: (res) => {
        if (!res?.data) return;
        this.options.set({ subjects: res.data.subjects, professors: res.data.professors, groups: [], students: [] });
        this.scope.set(res.data.scope ?? { all: false, items: [] });
      },
      // Sans référentiel l'écran reste utilisable : seuls les filtres perdent
      // leurs listes, et le périmètre reste inconnu — pas « vide ».
      error: () => {
        this.options.set({ subjects: [], groups: [], students: [], professors: [] });
        this.scope.set(null);
      },
    });
  }

  /** Public : le dialogue de rapport demande une relecture quand la base a changé. */
  load(): void {
    this.loading.set(true);
    this.errored.set(false);

    const { from, to } = this.bounds();
    this.service
      .search({
        from,
        to,
        professorId: this.filterProfessorId,
        subjectId: this.filterSubjectId,
        inspected: this.inspectedFilter === 'all' ? null : this.inspectedFilter === 'yes',
        page: this.page(),
        pageSize: PAGE_SIZE,
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.sessions.set(res?.data ?? []);
          this.total.set(res?.pagination?.total ?? 0);
        },
        error: () => {
          // Une liste vide reste vide : aucune séance de substitution.
          this.sessions.set([]);
          this.total.set(0);
          this.errored.set(true);
        },
      });
  }

  // ── Helpers de gabarit ─────────────────────────────────────

  // ── Enregistrement : lecture dans l'écran, jamais un lien qui télécharge ──
  readonly preview = signal<PreviewItem | null>(null);

  /**
   * Ouvre l'enregistrement dans l'aperçu.
   *
   * <p>L'URL présignée est de courte durée, porte son autorisation et se
   * sert en ligne : le lecteur la lit en flux, par plages — une heure de
   * vidéo se parcourt sans attendre. Elle n'est jamais remise au navigateur
   * comme une page, qui l'enregistrerait. Un lien externe part chez son
   * hébergeur, depuis l'aperçu.</p>
   */
  openRecording(s: InspectableSession): void {
    const target = this.rules.playable(s);
    if (!target) return;

    this.preview.set({
      title: target.title || s.title,
      category: target.category,
      meta: [s.consultantName, this.subjectsOf(s)].filter((x): x is string => !!x),
      url: target.previewUrl,
      externalUrl: target.previewUrl ? null : target.externalUrl,
    });
  }

  subjectsOf(s: InspectableSession): string {
    return this.subjectChoice.subjectsOf(s);
  }

  levelsOf(s: InspectableSession): string {
    return this.subjectChoice.levelsOf(s.subjects);
  }

  inspectorsOf(s: InspectableSession): string {
    return s.inspections.map((i) => i.inspectorName).join(', ');
  }


}

/** Le message du serveur, lisible par l'utilisateur ; sinon le libellé — jamais un texte technique. */
function messageFrom(err: unknown): string {
  const e = err as { error?: { message?: string } };
  return e?.error?.message || 'INSPECTION.SAVE_ERROR';
}
